import { Injectable, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';
import { AudienceService } from '../audience/audience.service';
import { Audience } from '../audience/components/audience.model';
import { CurrentSiteIdStore } from '../core/current-site-id.store';
import { TargetSite } from '../core/target-site';
import { AgentMentionOption } from './agent-mention.model';

// Supplies audiences for @-mention autocomplete. Cached per site (audiences are site-scoped, so a
// site switch invalidates the cache). Failures degrade to an empty list; the Agents backend
// re-validates every id.
@Injectable({ providedIn: 'root' })
export class AgentAudienceSource {
  private readonly audienceService = inject(AudienceService);
  private readonly currentSiteIdStore = inject(CurrentSiteIdStore);
  private readonly translate = inject(TranslateService);

  private cache: AgentMentionOption[] | null = null;
  private cacheSiteId: number | undefined;
  private inFlight: Promise<AgentMentionOption[]> | null = null;

  audiences(): Promise<AgentMentionOption[]> {
    const site = TargetSite.get() ?? this.currentSiteIdStore.get();
    if (this.cache && this.cacheSiteId === site) {
      return Promise.resolve(this.cache);
    }
    if (this.inFlight && this.cacheSiteId === site) {
      return this.inFlight;
    }
    this.cacheSiteId = site;
    this.inFlight = this.load();
    return this.inFlight;
  }

  private async load(): Promise<AgentMentionOption[]> {
    try {
      const audiences = await firstValueFrom(this.audienceService.getAllAudiences());
      this.cache = toOptions(audiences, this.translate.instant('agent.inspect.audience'));
      return this.cache;
    } catch {
      this.cache = null;
      return [];
    } finally {
      this.inFlight = null;
    }
  }
}

// Audience ids are Guids. The hint just names the kind; a Guid isn't meaningful to show at pick time.
function toOptions(audiences: readonly Audience[], hint: string): AgentMentionOption[] {
  const options: AgentMentionOption[] = [];
  for (const audience of audiences) {
    const id = audience.id;
    if (!id) {
      continue;
    }
    const label = (audience.name ?? '').trim() || id;
    options.push({ kind: 'audience', id, label, searchText: label.toLowerCase(), hint });
  }
  options.sort((a, b) => a.label.localeCompare(b.label));
  return options;
}
